import type { HeroStats } from "@/types";
import React from "react";

import { SupText } from "@repo/core-ui";
import CountUp from "react-countup";

import CredHomeHeroSuperSphere from "@/components/Cred/Home/Hero/SuperSphere";
import { Icons } from "../../UI/Icon";
import { formatCurrencyCompact } from "@/utils/formatters";

interface CredHomeHeroStatsProps {
  isLoading?: boolean;
  daysLeft?: number;
  totalBoost?: HeroStats["boosts"]["total"];
  totalCred?: HeroStats["cred"]["total"];
}

export default function CredHomeHeroStats({ isLoading, daysLeft, totalBoost, totalCred }: CredHomeHeroStatsProps) {
  return (
    <div className="relative z-10 flex w-full flex-col items-center lg:h-60 lg:w-[628px]">
      <div className="absolute top-1/2 h-[304px] w-56 -translate-y-1/2 transform bg-neutral-75 lg:hidden" />

      <div className="z-10 order-1 inline-flex h-[152px] w-56 flex-col items-start justify-center lg:absolute lg:left-[404px] lg:top-[48px]">
        {/* right-top text */}
        <div className="relative z-10 inline-flex min-h-[32px] w-full items-center justify-between self-stretch overflow-hidden whitespace-nowrap px-4 lg:pl-0 lg:pr-10">
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-b-neutral-75 border-r-neutral-75 lg:hidden" />
          <div className="flex h-8 flex-grow items-center justify-center bg-neutral-75 px-8 py-2 lg:justify-end lg:pr-0">
            <SupText variant="caption" className="leading-8 text-neutral-900">
              Currently
            </SupText>
          </div>
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-b-neutral-75 border-l-neutral-75" />
        </div>

        {/* Central part */}
        <div className="inline-flex h-[88px] flex-col items-start justify-start gap-2 self-stretch rounded-t-3xl border border-primary-500 bg-gradient-to-b from-primary-500/20 via-primary-500/40 via-[49%] to-primary-500/20 px-8 py-4 lg:rounded-br-3xl lg:rounded-tl-none">
          <div className="flex h-[72px] flex-col items-center justify-center gap-2 self-stretch lg:items-end">
            <SupText variant="caption" weight="bold" className="self-stretch text-center leading-4 text-primary-950 lg:text-right">
              Total boost
            </SupText>
            <div className="flex h-12 flex-col items-center justify-center self-stretch lg:items-end">
              {isLoading ? (
                <div className="my-1 inline-flex h-6 w-32 animate-pulse rounded-full bg-primary-900/50" />
              ) : (
                <SupText variant="caption" weight="bold" className="flex text-center text-[24px] leading-8 tracking-[2px] text-primary-900">
                  <Icons.star className="h-7 w-7" />
                  <CountUp decimals={2} start={0} duration={2} end={totalBoost as number} formattingFn={val => val.toLocaleString("en-US")} />X
                </SupText>
              )}
              <div>&nbsp;</div>
            </div>
          </div>
        </div>

        {/* Right bottom text */}
        <div className="relative z-10 inline-flex min-h-[32px] w-full items-center justify-between self-stretch overflow-hidden whitespace-nowrap px-4 lg:pl-0 lg:pr-10">
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-r-neutral-75 border-t-neutral-75 lg:hidden" />
          <div className="flex h-8 flex-grow items-center justify-center bg-neutral-75 px-4 py-2 lg:justify-end lg:pr-0">
            {isLoading ? (
              <div className="my-1 inline-flex h-4 w-16 shrink-0 animate-pulse rounded-full bg-neutral-900/50" />
            ) : (daysLeft ?? 1) > 0 ? (
              <SupText variant="caption" weight="bold" className="leading-8 text-neutral-900">
                {daysLeft} days left
              </SupText>
            ) : (
              <SupText variant="caption" weight="bold" className="leading-8 text-primary-925">
                Finished
              </SupText>
            )}
          </div>
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-l-neutral-75 border-t-neutral-75" />
        </div>
      </div>

      <div className="relative z-20 order-2 h-60 w-60 rounded-full border-[.83px] border-primary-25 bg-primary shadow-[0,0,20px,primary-25] lg:absolute lg:left-[194px] lg:top-0">
        <div className="absolute left-[43.33px] top-[42.50px] h-[154.17px] w-[154.17px] rounded-[833.33px]">
          <CredHomeHeroSuperSphere />
        </div>
        <div className="absolute left-0 top-0 h-60 w-60 rounded-full mix-blend-exclusion" />
      </div>

      <div className="z-10 order-3 inline-flex h-[152px] w-56 flex-col items-end justify-center lg:absolute lg:left-0 lg:top-[48px]">
        {/* left-top text */}
        <div className="relative z-10 inline-flex min-h-[32px] w-full items-center justify-between self-stretch overflow-hidden whitespace-nowrap px-4 lg:pl-10 lg:pr-0">
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-b-neutral-75 border-r-neutral-75" />
          <div className="flex h-8 flex-grow items-center justify-center bg-neutral-75 px-8 py-2 lg:justify-start lg:pl-0">
            <SupText variant="caption" className="leading-8 text-neutral-900">
              Overall
            </SupText>
          </div>
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-b-neutral-75 border-l-neutral-75 lg:hidden" />
        </div>

        {/* Central part */}
        <div className="inline-flex h-[88px] flex-col items-start justify-start gap-2 self-stretch rounded-b-3xl border border-primary-500 bg-gradient-to-b from-primary-500/20 via-primary-500/40 via-[49%] to-primary-500/20 px-8 py-4 lg:rounded-br-none lg:rounded-tl-3xl">
          <div className="flex h-[72px] flex-col items-center justify-center gap-2 self-stretch lg:items-start">
            <SupText variant="caption" weight="bold" className="self-stretch text-center leading-4 text-primary-950 lg:text-left">
              YOUR CRED
            </SupText>
            <div className="flex h-12 flex-col items-center justify-center self-stretch lg:items-start">
              {isLoading ? (
                <div className="my-1 inline-flex h-6 w-32 animate-pulse rounded-full bg-primary-900/50" />
              ) : (
                <SupText variant="caption" weight="bold" className="flex space-x-2 text-center text-[24px] leading-8 tracking-[2px] text-primary-900">
                  <img src="/assets/img/cred/cred-light.svg" alt="Cred" />
                  <CountUp decimals={0} start={0} duration={2} end={totalCred as number} formattingFn={val => formatCurrencyCompact(val, 0, 0, "decimal", "compact")} />
                </SupText>
              )}
              <div>&nbsp;</div>
            </div>
          </div>
        </div>

        {/* left-bottom text */}
        <div className="relative z-10 inline-flex min-h-[32px] w-full items-center justify-between self-stretch overflow-hidden whitespace-nowrap px-4 lg:pl-10 lg:pr-0">
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-r-neutral-75 border-t-neutral-75" />
          <div className="flex h-8 flex-grow items-center justify-center bg-neutral-75 px-4 py-2 lg:justify-start lg:pl-0">
            <SupText variant="caption" weight="bold" className="leading-8 text-neutral-900">
              earned cred
            </SupText>
          </div>
          <div className="h-0 w-0 shrink-0 border-[16px] border-transparent border-l-neutral-75 border-t-neutral-75 lg:hidden" />
        </div>
      </div>
    </div>
  );
}
